import { format, isToday } from 'date-fns';
import * as locales from 'date-fns/locale';
import { useRouter } from 'next/router';
import { useTranslation } from 'react-i18next';

import { useCreateEditContractUtils } from '@/modules/workforce/Contracts/components/CreateAndEdit/logic';
import { useContractDetails } from '@/modules/workforce/Contracts/components/Details/logic';
import EndDeleteContract from '@/modules/workforce/Contracts/components/Details/Modals/EndDeleteContract';
import { IContracts } from '@/modules/workforce/Contracts/types';
import { Button, Typography } from '@/shared/components/atoms';
import Alert from '@/shared/components/atoms/Alert';
import DetailsPageHeader from '@/shared/components/molecules/DetailsPageHeader';
import { useModal } from '@/shared/hooks/context/modalContext/useModal';

import EditIcon from '~/images/shared/edit.svg';

const contractStatuses: Record<
  IContracts.IContractStatusLabel,
  IContracts.IContractStatus
> = {
  Active: 1,
  Upcoming: 2,
  Past: 3,
};

const ContractDetailsHeader: React.FC<{
  contract: IContracts.IContractDetails;
}> = ({ contract }) => {
  const {
    t,
    i18n: { language },
  } = useTranslation();

  const { query } = useRouter();

  const { openModal } = useModal();

  const { getContractStatus, checkUnitMismatch } = useContractDetails();

  const { openEditContractModal, checkCanEditContract } =
    useCreateEditContractUtils();

  return (
    <div className='flex flex-col gap-24'>
      <div className=' flex items-center justify-between'>
        <Typography variant='h1' weight='bold'>{`${t('Contract')} #${
          query?.id
        }`}</Typography>

        <div className='flex items-center gap-8'>
          {contract.status !== 'Past' && !isToday(new Date(contract.endDate)) && (
            <Button
              variant='text'
              size='none'
              className='h-40 !rounded-xl border-xs border-error-400 px-24 !text-error-400'
              onClick={() =>
                openModal(
                  <EndDeleteContract
                    supplierAssignmentId={contract.supplierAssignmentId}
                    status={contract.status as 'Active' | 'Upcoming'}
                  />
                )
              }
            >
              {t(
                contract.status === 'Active'
                  ? 'End contract'
                  : 'Delete contract'
              )}
            </Button>
          )}
          {checkCanEditContract({
            ...contract,
            status: contractStatuses[contract.status],
          }) && (
            <Button
              size='none'
              className='flex h-40 items-center gap-4 !rounded-xl px-16 py-8'
              onClick={() =>
                openEditContractModal(contract.supplierAssignmentId)
              }
            >
              <>
                <EditIcon width='24' height='24' />
                {t('edit')}
              </>
            </Button>
          )}
        </div>
      </div>

      {!contract.contractStatus && (
        <Alert
          type='warning'
          message={t('contract-auto-generated-alert', {
            supplierName: contract.supplierName,
            jobName: contract.jobName,
            createdAt: format(new Date(contract.createdAt), 'dd/MM/yyyy', {
              locale: locales[language as keyof typeof locales],
            }),
          })}
        />
      )}

      {checkUnitMismatch(
        contract.contractStatus,
        contract.dailyWage,
        contract.pricingUnits
      ) && (
        <Alert
          type='warning'
          message={t(
            'This contract contains a productivity unit without a unit value that was added by the supervisor.'
          )}
        />
      )}

      <DetailsPageHeader
        title={contract.jobName}
        status={getContractStatus(contract.status)}
        icon={contract.cropImage}
        timeline={[
          {
            title: `${t('date-creation')}:`,
            date: format(new Date(contract.createdAt), 'd MMM yyyy', {
              locale: locales[language as keyof typeof locales],
            }),
          },
        ]}
        imageProps={{
          width: 52,
          height: 52,
        }}
        statusPlacement='right'
      />
    </div>
  );
};

export default ContractDetailsHeader;
